/**
 * <keystone-field-date> \u2014 date input.
 *
 * Maps onto JSON Schema `type: "string"` + `format: "date"`. Renders an
 * `<input type="date">`; HTML5 native handles parsing, validation, and
 * the picker UI. Internal value is the ISO-8601 date string the input
 * produces (e.g. "2026-05-29") which is also what JSON Schema's
 * `format: "date"` expects on the wire \u2014 no coercion needed at submit.
 *
 * `format: "date-time"` is intentionally NOT handled here \u2014 native
 * <input type="datetime-local"> is browser-inconsistent and the
 * value-shape doesn't round-trip cleanly with ISO-8601 with timezone.
 * <keystone-field-text> will continue to handle date-time strings; a
 * proper <keystone-field-datetime> can land later if it matters.
 */

import { LitElement, html, nothing } from "lit";
import { customElement, property } from "lit/decorators.js";
import { ifDefined } from "lit/directives/if-defined.js";
import { fieldStyles, humaniseFieldName } from "./base.js";

@customElement("keystone-field-date")
export class KeystoneFieldDate extends LitElement {
  static override styles = fieldStyles;

  @property({ type: String }) name = "";
  @property({ type: String }) label = "";
  @property({ type: String }) value = "";
  @property({ type: Boolean }) required = false;
  @property({ type: String }) error: string | null = null;
  @property({ type: Boolean }) disabled = false;

  @property({ type: String }) min: string | null = null;
  @property({ type: String }) max: string | null = null;

  override updated(): void {
    if (this.error) this.setAttribute("data-error", "");
    else this.removeAttribute("data-error");
  }

  private onInput(e: Event): void {
    const input = e.target as HTMLInputElement;
    this.value = input.value;
    this.dispatchEvent(
      new CustomEvent("keystone-field-input", {
        detail: { name: this.name, value: input.value },
        bubbles: true,
        composed: true,
      }),
    );
  }

  override render() {
    const label = this.label || humaniseFieldName(this.name);
    const inputId = `input-${this.name}`;

    return html`
      <label for=${inputId}>
        ${label}${this.required
          ? html`<span class="required-marker" aria-hidden="true">*</span>`
          : ""}
      </label>
      <input
        id=${inputId}
        name=${this.name}
        type="date"
        .value=${this.value}
        ?required=${this.required}
        ?disabled=${this.disabled}
        min=${ifDefined(this.min ?? undefined)}
        max=${ifDefined(this.max ?? undefined)}
        aria-invalid=${this.error ? "true" : "false"}
        aria-describedby=${this.error ? `${inputId}-error` : nothing}
        @input=${this.onInput}
      />
      ${this.error
        ? html`<p class="error" id="${inputId}-error" role="alert">
            ${this.error}
          </p>`
        : ""}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "keystone-field-date": KeystoneFieldDate;
  }
}
